import React, { useState, useMemo } from 'react';
import {
  TableRow, TableCell, Box, Avatar, Typography, Chip, TextField, MenuItem,
  IconButton, Tooltip, Badge, Button, Collapse, Stack, Paper, TablePagination
} from '@mui/material';
import {
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as SparkleIcon,
  Add as AddIcon,
  MergeType as MergeIcon,
  AssignmentInd as AssignIcon,
  WarningAmber as WarningIcon,
  FlightTakeoff as TripIcon
} from '@mui/icons-material';
import moment from 'moment';
import { STATUS_COLORS } from './utils';

const CustomerGroupRow = ({
  group,
  userRole,
  users,
  onEdit,
  onDelete,
  onCallHistory,
  onGenerateQuote,
  onQuickAssign,
  onViewQuotations,
  onChangeStatus,
  onOpenUnifyDialog,
  onOpenMergeDialog,
  onAddNewTripForGuest,
  defaultExpanded = false
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [subPage, setSubPage] = useState(0);
  const [subRowsPerPage, setSubRowsPerPage] = useState(5);

  const {
    guest_name,
    guest_phone,
    guest_email,
    leads,
    inquiriesCount,
    isMultiLead,
    statusCounts,
    assignedUsers,
    hasMultipleAssignees,
    totalQuotes,
    latestCreatedAt
  } = group;

  const isAdminView = ['Admin', 'Admin-a', 'Sales Manager'].includes(userRole);
  const singleLead = leads[0];

  // Pre-filter sales reps once instead of in every row render
  const salesUsers = useMemo(() => {
    return users.filter(u => ['Sales Executive', 'Sales Team Lead', 'Sales Manager'].includes(u.permission));
  }, [users]);

  // Paginated sub-leads for fast rendering
  const paginatedLeads = useMemo(() => {
    if (!expanded) return [];
    return leads.slice(subPage * subRowsPerPage, subPage * subRowsPerPage + subRowsPerPage);
  }, [leads, expanded, subPage, subRowsPerPage]);

  const toggleExpand = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setExpanded(prev => !prev);
  };

  return (
    <React.Fragment>
      {/* ── Main Customer Row ── */}
      <TableRow
        hover
        sx={{
          bgcolor: expanded ? '#f8fafc' : 'inherit',
          transition: 'background-color 0.15s ease',
          borderLeft: isMultiLead
            ? (hasMultipleAssignees ? '4px solid #f59e0b' : '4px solid #3b82f6')
            : '4px solid transparent'
        }}
      >
        {/* 1. Guest Profile */}
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isMultiLead ? (
              <IconButton
                size="small"
                onClick={toggleExpand}
                sx={{
                  bgcolor: expanded ? '#e2e8f0' : '#f1f5f9',
                  color: expanded ? '#1e293b' : '#64748b',
                  '&:hover': { bgcolor: '#cbd5e1' }
                }}
              >
                {expanded ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 28 }} />
            )}

            <Avatar
              sx={{
                width: 38,
                height: 38,
                bgcolor: isMultiLead ? '#e0e7ff' : '#e3f2fd',
                color: isMultiLead ? '#4338ca' : '#1565c0',
                fontWeight: 700,
                fontSize: 15,
                border: isMultiLead ? '2px solid #818cf8' : 'none'
              }}
            >
              {guest_name?.charAt(0)?.toUpperCase()}
            </Avatar>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={700} color="#1e293b">
                  {guest_name}
                </Typography>
                {isMultiLead && (
                  <Tooltip title={`Customer has submitted ${inquiriesCount} separate trip inquiries`}>
                    <Chip
                      size="small"
                      icon={<SparkleIcon sx={{ fontSize: '13px !important' }} />}
                      label={`${inquiriesCount} Trips`}
                      onClick={toggleExpand}
                      sx={{
                        height: 22,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                        color: '#fff',
                        boxShadow: '0 2px 6px rgba(79, 70, 229, 0.25)',
                        '& .MuiChip-icon': { color: '#fff !important' },
                        '& .MuiChip-label': { px: 0.8 }
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {guest_email || '—'}
              </Typography>
            </Box>
          </Box>
        </TableCell>

        {/* 2. Phone */}
        <TableCell>
          <Typography variant="body2" fontWeight={500} color="#334155">
            {guest_phone}
          </Typography>
        </TableCell>

        {/* 3. Destination / Pax */}
        <TableCell>
          {isMultiLead ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5, maxWidth: { xs: 220, md: 360 } }}>
              {leads.slice(0, 2).map((lead, i) => (
                <Chip
                  key={lead._id || i}
                  size="small"
                  label={`${lead.destination || 'N/A'}${lead.pax ? ` (${lead.pax}p)` : ''}`}
                  sx={{
                    height: 22,
                    fontSize: '0.72rem',
                    bgcolor: '#f1f5f9',
                    color: '#334155',
                    fontWeight: 600,
                    border: '1px solid #e2e8f0'
                  }}
                />
              ))}
              {leads.length > 2 && (
                <Tooltip
                  title={leads.slice(2).map(l => `${l.destination || 'N/A'} (${l.pax || 0} Pax)`).join(', ')}
                >
                  <Chip
                    size="small"
                    label={`+${leads.length - 2} more`}
                    onClick={toggleExpand}
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      bgcolor: '#e2e8f0',
                      color: '#475569',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          ) : (
            <Box>
              <Typography variant="body2" fontWeight={600} color="#334155">
                {singleLead.destination || 'N/A'}
              </Typography>
              {singleLead.pax > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {singleLead.pax} Pax{singleLead.kids_above_5 > 0 ? ` + ${singleLead.kids_above_5} Kids` : ''}
                </Typography>
              )}
            </Box>
          )}
        </TableCell>

        {/* 4. Status */}
        <TableCell>
          {isMultiLead ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
              {Object.entries(statusCounts).map(([statusName, count]) => (
                <Chip
                  key={statusName}
                  size="small"
                  label={`${statusName} (${count})`}
                  color={STATUS_COLORS[statusName] || 'default'}
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                />
              ))}
            </Stack>
          ) : (
            <TextField
              select
              size="small"
              value={singleLead.status || 'New'}
              onChange={(e) => onChangeStatus(singleLead._id, e.target.value)}
              sx={{
                minWidth: 120,
                '& .MuiSelect-select': {
                  py: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: STATUS_COLORS[singleLead.status] === 'primary' ? 'primary.main' :
                    STATUS_COLORS[singleLead.status] === 'warning' ? 'warning.main' :
                    STATUS_COLORS[singleLead.status] === 'success' ? 'success.main' :
                    STATUS_COLORS[singleLead.status] === 'error' ? 'error.main' : 'inherit'
                }
              }}
            >
              <MenuItem value="New" sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.875rem' }}>New</MenuItem>
              <MenuItem value="In Progress" sx={{ color: 'warning.main', fontWeight: 600, fontSize: '0.875rem' }}>In Progress</MenuItem>
              <MenuItem value="Converted" sx={{ color: 'success.main', fontWeight: 600, fontSize: '0.875rem' }}>Converted</MenuItem>
              <MenuItem value="Lost" sx={{ color: 'error.main', fontWeight: 600, fontSize: '0.875rem' }}>Lost</MenuItem>
            </TextField>
          )}
        </TableCell>

        {/* 5. Assigned To */}
        <TableCell>
          {isMultiLead ? (
            <Box>
              {hasMultipleAssignees ? (
                <Tooltip title={`Inquiries split between: ${assignedUsers.map(u => u.fullName).join(', ')}. Click Unify to assign all to one agent.`}>
                  <Chip
                    icon={<WarningIcon sx={{ fontSize: '14px !important', color: '#b45309 !important' }} />}
                    label={`${assignedUsers.length} Agents Assigned`}
                    size="small"
                    color="warning"
                    variant="filled"
                    onClick={() => isAdminView && onOpenUnifyDialog(group)}
                    sx={{
                      height: 24,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      cursor: isAdminView ? 'pointer' : 'default',
                      bgcolor: '#fef3c7',
                      color: '#92400e',
                      border: '1px solid #fde68a'
                    }}
                  />
                </Tooltip>
              ) : assignedUsers.length > 0 ? (
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {assignedUsers.map(u => (
                    <Chip
                      key={u._id || u.id}
                      avatar={<Avatar sx={{ width: 18, height: 18 }}>{u.fullName?.charAt(0)}</Avatar>}
                      label={u.fullName}
                      size="small"
                      variant="outlined"
                      sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600 }}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">Unassigned</Typography>
              )}
            </Box>
          ) : (
            isAdminView ? (
              <TextField
                select size="small"
                SelectProps={{ multiple: true }}
                value={Array.isArray(singleLead.assigned_to) ? singleLead.assigned_to.map(u => u._id || u.id || u) : []}
                onChange={(e) => onQuickAssign(singleLead._id, e.target.value)}
                sx={{ minWidth: 150 }}
              >
                {salesUsers.map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>
                ))}
              </TextField>
            ) : (
              Array.isArray(singleLead.assigned_to) && singleLead.assigned_to.length > 0
                ? <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {singleLead.assigned_to.map(u => (
                      <Chip key={u._id || u.id || u} avatar={<Avatar>{u.fullName?.charAt(0)}</Avatar>} label={u.fullName} size="small" variant="outlined" />
                    ))}
                  </Stack>
                : <Typography variant="caption" color="text.secondary">—</Typography>
            )
          )}
        </TableCell>

        {/* 6. Date Added */}
        <TableCell>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {latestCreatedAt ? moment(latestCreatedAt).format('DD MMM YYYY') : '—'}
          </Typography>
        </TableCell>

        {/* 7. Quotes */}
        <TableCell align="center">
          {isMultiLead ? (
            <Badge badgeContent={totalQuotes} showZero color="primary" sx={{ '& .MuiBadge-badge': { right: -3, top: -3 } }}>
              <Button
                size="small"
                variant="outlined"
                onClick={toggleExpand}
                sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'none' }}
              >
                {expanded ? 'Hide Trips' : `View ${inquiriesCount} Trips`}
              </Button>
            </Badge>
          ) : (
            <>
              <Badge badgeContent={singleLead.quotationsCount || 0} showZero color="primary" sx={{ '& .MuiBadge-badge': { right: -3, top: -3 } }}>
                <Button size="small" variant="outlined" onClick={() => onGenerateQuote(singleLead)}>Generate</Button>
              </Badge>
              {singleLead.quotationsCount > 0 && (
                <Button size="small" variant="text" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }} onClick={() => onViewQuotations(singleLead)}>
                  View All
                </Button>
              )}
            </>
          )}
        </TableCell>

        {/* 8. Actions */}
        <TableCell align="center">
          {isMultiLead ? (
            <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
              <Tooltip title={`Add another trip inquiry for ${guest_name}`}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onAddNewTripForGuest(group)}
                  sx={{ bgcolor: '#eff6ff', '&:hover': { bgcolor: '#dbeafe' } }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {isAdminView && (
                <Tooltip title="Unify assignment of all trips to 1 agent">
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => onOpenUnifyDialog(group)}
                    sx={{ bgcolor: '#faf5ff', '&:hover': { bgcolor: '#f3e8ff' } }}
                  >
                    <AssignIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {isAdminView && inquiriesCount > 1 && (
                <Tooltip title="Merge duplicate trips for this customer">
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => onOpenMergeDialog(group)}
                    sx={{ bgcolor: '#fffbeb', '&:hover': { bgcolor: '#fef3c7' } }}
                  >
                    <MergeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={expanded ? 'Collapse' : 'Expand all trip inquiries'}>
                <IconButton
                  size="small"
                  onClick={toggleExpand}
                  sx={{ bgcolor: '#f8fafc', '&:hover': { bgcolor: '#e2e8f0' } }}
                >
                  {expanded ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <>
              <Tooltip title="Call History">
                <IconButton color="info" size="small" onClick={() => onCallHistory(singleLead)}>
                  <Badge
                    badgeContent={singleLead.call_history?.length || 0}
                    showZero={false}
                    color="info"
                    sx={{ '& .MuiBadge-badge': { right: -3, top: -3 } }}
                  >
                    <PhoneIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton color="primary" size="small" onClick={() => onEdit(singleLead)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton color="error" size="small" onClick={() => onDelete(singleLead._id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </TableCell>
      </TableRow>

      {/* ── Collapsible Sub-Table for Multi-Inquiry Customer ── */}
      {isMultiLead && (
        <TableRow sx={{ bgcolor: '#f8fafc' }}>
          <TableCell colSpan={8} sx={{ py: 0, px: { xs: 1, sm: 3 }, borderBottom: expanded ? '1px solid #e2e8f0' : 'none' }}>
            <Collapse in={expanded} timeout={200} unmountOnExit>
              <Paper
                variant="outlined"
                sx={{
                  my: 2,
                  p: 2.5,
                  borderRadius: 3,
                  bgcolor: '#ffffff',
                  boxShadow: '0 4px 16px -2px rgba(0,0,0,0.06)',
                  border: '1px solid #e2e8f0'
                }}
              >
                {/* Header inside drawer */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      p: 1, borderRadius: 2, bgcolor: '#e0e7ff', color: '#4338ca',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <TripIcon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={800} color="#0f172a">
                        Trip Inquiries for {guest_name} ({inquiriesCount} total)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Showing page {subPage + 1} of {Math.ceil(inquiriesCount / subRowsPerPage)}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => onAddNewTripForGuest(group)}
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                      }}
                    >
                      Add New Trip
                    </Button>
                    {isAdminView && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={<AssignIcon />}
                        onClick={() => onOpenUnifyDialog(group)}
                        sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'none' }}
                      >
                        Unify Assignee
                      </Button>
                    )}
                    {isAdminView && inquiriesCount > 1 && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<MergeIcon />}
                        onClick={() => onOpenMergeDialog(group)}
                        sx={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'none' }}
                      >
                        Merge Trips
                      </Button>
                    )}
                  </Stack>
                </Box>

                {/* Sub-table with paginated rows for maximum speed */}
                <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #f1f5f9' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', color: '#64748b', borderBottom: '1px solid #e2e8f0', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>
                        <th style={{ padding: '10px 16px' }}>Trip Destination & Pax</th>
                        <th style={{ padding: '10px 16px' }}>Travel Date</th>
                        <th style={{ padding: '10px 16px' }}>Status</th>
                        <th style={{ padding: '10px 16px' }}>Assigned Agent</th>
                        <th style={{ padding: '10px 16px' }}>Created Date</th>
                        <th style={{ padding: '10px 16px', textAlign: 'center' }}>Quotes</th>
                        <th style={{ padding: '10px 16px', textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedLeads.map((lead, idx) => (
                        <tr
                          key={lead._id || idx}
                          style={{
                            borderBottom: idx === paginatedLeads.length - 1 ? 'none' : '1px solid #f1f5f9',
                            backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa'
                          }}
                        >
                          {/* Destination & Pax */}
                          <td style={{ padding: '12px 16px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={700} color="#1e293b">
                                {lead.destination || 'N/A'}
                              </Typography>
                              {lead.pax > 0 && (
                                <Chip
                                  size="small"
                                  label={`${lead.pax} Pax`}
                                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#e0f2fe', color: '#0369a1' }}
                                />
                              )}
                            </Box>
                            {lead.remarks && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3, maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                💬 {lead.remarks}
                              </Typography>
                            )}
                          </td>

                          {/* Travel Date */}
                          <td style={{ padding: '12px 16px' }}>
                            <Typography variant="body2" color="#475569">
                              {lead.arrival_date ? moment(lead.arrival_date).format('DD MMM YYYY') : '—'}
                            </Typography>
                          </td>

                          {/* Status Dropdown */}
                          <td style={{ padding: '12px 16px' }}>
                            <TextField
                              select
                              size="small"
                              value={lead.status || 'New'}
                              onChange={(e) => onChangeStatus(lead._id, e.target.value)}
                              sx={{
                                minWidth: 120,
                                '& .MuiSelect-select': {
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: STATUS_COLORS[lead.status] === 'primary' ? 'primary.main' :
                                    STATUS_COLORS[lead.status] === 'warning' ? 'warning.main' :
                                    STATUS_COLORS[lead.status] === 'success' ? 'success.main' :
                                    STATUS_COLORS[lead.status] === 'error' ? 'error.main' : 'inherit'
                                }
                              }}
                            >
                              <MenuItem value="New" sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.875rem' }}>New</MenuItem>
                              <MenuItem value="In Progress" sx={{ color: 'warning.main', fontWeight: 600, fontSize: '0.875rem' }}>In Progress</MenuItem>
                              <MenuItem value="Converted" sx={{ color: 'success.main', fontWeight: 600, fontSize: '0.875rem' }}>Converted</MenuItem>
                              <MenuItem value="Lost" sx={{ color: 'error.main', fontWeight: 600, fontSize: '0.875rem' }}>Lost</MenuItem>
                            </TextField>
                          </td>

                          {/* Assigned Agent Dropdown */}
                          <td style={{ padding: '12px 16px' }}>
                            {isAdminView ? (
                              <TextField
                                select
                                size="small"
                                SelectProps={{ multiple: true }}
                                value={Array.isArray(lead.assigned_to) ? lead.assigned_to.map(u => u._id || u.id || u) : []}
                                onChange={(e) => onQuickAssign(lead._id, e.target.value)}
                                sx={{ minWidth: 140 }}
                              >
                                {salesUsers.map(u => (
                                  <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>
                                ))}
                              </TextField>
                            ) : (
                              Array.isArray(lead.assigned_to) && lead.assigned_to.length > 0 ? (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                  {lead.assigned_to.map(u => (
                                    <Chip key={u._id || u.id || u} avatar={<Avatar>{u.fullName?.charAt(0)}</Avatar>} label={u.fullName} size="small" variant="outlined" />
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )
                            )}
                          </td>

                          {/* Created Date */}
                          <td style={{ padding: '12px 16px' }}>
                            <Typography variant="body2" color="text.secondary">
                              {lead.createdAt ? moment(lead.createdAt).format('DD MMM YYYY') : '—'}
                            </Typography>
                          </td>

                          {/* Quotes */}
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <Badge badgeContent={lead.quotationsCount || 0} showZero color="primary" sx={{ '& .MuiBadge-badge': { right: -3, top: -3 } }}>
                              <Button size="small" variant="outlined" onClick={() => onGenerateQuote(lead)} sx={{ fontSize: '0.75rem', py: 0.3 }}>
                                Generate
                              </Button>
                            </Badge>
                            {lead.quotationsCount > 0 && (
                              <Button size="small" variant="text" sx={{ display: 'block', mt: 0.5, mx: 'auto', fontSize: '0.7rem' }} onClick={() => onViewQuotations(lead)}>
                                View All ({lead.quotationsCount})
                              </Button>
                            )}
                          </td>

                          {/* Actions */}
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="Call History">
                                <IconButton color="info" size="small" onClick={() => onCallHistory(lead)}>
                                  <Badge badgeContent={lead.call_history?.length || 0} showZero={false} color="info" sx={{ '& .MuiBadge-badge': { right: -3, top: -3 } }}>
                                    <PhoneIcon fontSize="small" />
                                  </Badge>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Trip">
                                <IconButton color="primary" size="small" onClick={() => onEdit(lead)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Trip">
                                <IconButton color="error" size="small" onClick={() => onDelete(lead._id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>

                {/* Sub-pagination if customer has more than 5 trips */}
                {leads.length > 5 && (
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={leads.length}
                    rowsPerPage={subRowsPerPage}
                    page={subPage}
                    onPageChange={(e, p) => setSubPage(p)}
                    onRowsPerPageChange={(e) => {
                      setSubRowsPerPage(parseInt(e.target.value, 10));
                      setSubPage(0);
                    }}
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count} trips`}
                    sx={{ mt: 1, '& .MuiToolbar-root': { minHeight: 40 } }}
                  />
                )}
              </Paper>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};

export default React.memo(CustomerGroupRow);
